import {
  DIAGNOSIS_MONITOR_FROM,
  DIAGNOSIS_STATUS_LABELS,
  diagnosisArchiveInputSchema,
  diagnosisItemSchema,
  formatDecimal,
  matrixListSchema,
  objectiveOptionsSchema,
  type MatrixItem,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ClipboardList,
  Eye,
  FolderKanban,
  Grid3x3,
  ListChecks,
  Lock,
  Radar,
  Rocket,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { NativeSelect } from '@/components/ui/select';
import {
  ItemSummary,
  RelateObjective,
  STATUS_LIGHT,
  TransformInitiativeDialog,
} from '@/features/diagnosis/ItemParts';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const score = (v: number) => formatDecimal(Math.round(v * 100) / 100);

/**
 * Diagnóstico › Matriz de priorização (T-161b, RF-12, RF-13, RN-25, PRD §11.5; DEC-062): os itens enviados à matriz
 * em ordem de prioridade (impacto × urgência ÷ esforço), a matriz gráfica Impacto × Urgência com bolhas pelo esforço
 * e as saídas de cada item.
 */
export function MatrizPriorizacaoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Matriz de priorização"
        description="O que tratar primeiro: os problemas do diagnóstico ordenados por impacto × urgência ÷ esforço."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="A matriz pertence a um projeto. Quando a empresa tiver um projeto em andamento, ela aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <MatrixSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function MatrixSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/diagnosis`;
  const [params, setParams] = useSearchParams();
  const [departmentId, setDepartmentId] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const ids = { department: useId(), unit: useId() };
  const itemId = params.get('item');
  const fetchMatrix = (query: { departmentId?: string; businessUnitId?: string }, signal: AbortSignal) =>
    client.request(`${base}/matrix`, { query, schema: matrixListSchema, signal });
  // As opções dos filtros vêm da matriz inteira (as áreas e unidades que têm itens nela).
  const all = useQuery({
    queryKey: ['diagnosis', projectId, 'matrix', {}],
    queryFn: ({ signal }) => fetchMatrix({}, signal),
  });
  const filters = {
    ...(departmentId ? { departmentId } : {}),
    ...(businessUnitId ? { businessUnitId } : {}),
  };
  const list = useQuery({
    queryKey: ['diagnosis', projectId, 'matrix', filters],
    queryFn: ({ signal }) => fetchMatrix(filters, signal),
    placeholderData: keepPreviousData,
  });
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['diagnosis', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['action-plans', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['initiatives', projectId] }),
    ]);
  const openItem = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('item', id);
        else next.delete('item');
        return next;
      },
      { replace: true },
    );
  const departments = uniqueBy((all.data?.items ?? []).map((i) => i.department));
  const units = uniqueBy((all.data?.items ?? []).flatMap((i) => (i.businessUnit ? [i.businessUnit] : [])));
  const filtered = Boolean(departmentId || businessUnitId);
  const current = [...(list.data?.items ?? []), ...(all.data?.items ?? [])].find((i) => i.id === itemId);

  return (
    <div className="mt-6 flex flex-col gap-5">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: a matriz fica disponível só para consulta.
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Filter id={ids.department} label="Área" value={departmentId} onChange={setDepartmentId}>
          <option value="">Todas as áreas</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Filter>
        <Filter id={ids.unit} label="Unidade" value={businessUnitId} onChange={setBusinessUnitId}>
          <option value="">Todas as unidades</option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Filter>
      </div>

      {list.isPending ? (
        <LoadingState rows={5} label="Carregando a matriz" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Grid3x3}
          title={filtered ? 'Nenhum item nesta área ou unidade' : 'Nenhum item na matriz ainda'}
          description={
            filtered
              ? 'A matriz tem itens, mas nenhum da área ou unidade escolhida.'
              : 'Os itens entram na matriz pelo Diagnóstico: abra um item e use "Enviar para a matriz".'
          }
          action={
            filtered ? (
              <Button
                variant="outline"
                onClick={() => {
                  setDepartmentId('');
                  setBusinessUnitId('');
                }}
              >
                Limpar filtros
              </Button>
            ) : canDo('diagnostico', 'view') ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}/diagnostico?projeto=${projectId}`}>
                  <ClipboardList aria-hidden />
                  Ir para o Diagnóstico
                </Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-6" aria-busy={list.isFetching}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {list.data.items.length === list.data.total
              ? `${list.data.total} ${list.data.total === 1 ? 'item' : 'itens'} na matriz`
              : `${list.data.items.length} de ${list.data.total} itens da matriz`}
          </p>
          <MatrixChart items={list.data.items} onOpen={openItem} />
          <section aria-label="Ranking da matriz" className="flex flex-col gap-2">
            <h2 className="text-base font-bold">Ranking</h2>
            {/* Desktop: tabela; celular: cartões com os mesmos dados. */}
            <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Itens da matriz em ordem de prioridade: impacto × urgência ÷ esforço
                </caption>
                <thead className="bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2">
                      Prioridade
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Problema
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Impacto
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Urgência
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Esforço
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Pontuação
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((i) => (
                    <tr key={i.id} className="border-t border-border">
                      <td className="px-3 py-2 text-lg font-bold tabular">{i.priority}º</td>
                      <th scope="row" className="px-3 py-2 text-left font-semibold">
                        {i.title}
                        <span className="block text-xs font-normal text-muted-foreground">
                          {i.department.name}
                          {i.businessUnit ? ` · ${i.businessUnit.name}` : ''}
                        </span>
                      </th>
                      <td className="px-3 py-2 text-right tabular">{i.impact}</td>
                      <td className="px-3 py-2 text-right tabular">{i.urgency}</td>
                      <td className="px-3 py-2 text-right tabular">{i.effort}</td>
                      <td className="px-3 py-2 text-right tabular">{score(i.score)}</td>
                      <td className="px-3 py-2">
                        <StatusBadge
                          status={STATUS_LIGHT[i.status]}
                          label={DIAGNOSIS_STATUS_LABELS[i.status]}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="outline" size="sm" onClick={() => openItem(i.id)}>
                          <Eye aria-hidden />
                          Abrir <span className="sr-only">{i.title}</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ol aria-label="Itens da matriz" className="flex flex-col gap-2 md:hidden">
              {list.data.items.map((i) => (
                <li key={i.id} className="rounded-lg border border-border bg-surface p-4">
                  <article aria-label={`${i.priority}º ${i.title}`} className="flex flex-col gap-2">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl leading-none font-bold tabular">{i.priority}º</span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold break-words">{i.title}</h3>
                        <p className="text-xs text-muted-foreground">
                          {i.department.name}
                          {i.businessUnit ? ` · ${i.businessUnit.name}` : ''}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground tabular">
                      Impacto {i.impact} · Urgência {i.urgency} · Esforço {i.effort} · Pontuação{' '}
                      {score(i.score)}
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <StatusBadge
                        status={STATUS_LIGHT[i.status]}
                        label={DIAGNOSIS_STATUS_LABELS[i.status]}
                      />
                      <Button variant="outline" size="sm" onClick={() => openItem(i.id)}>
                        <Eye aria-hidden />
                        Abrir <span className="sr-only">{i.title}</span>
                      </Button>
                    </div>
                  </article>
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}

      <MatrixItemDialog
        base={base}
        organizationId={organizationId}
        projectId={projectId}
        itemId={itemId}
        listed={current}
        matrix={
          all.isError ? { error: all.error, retry: () => void all.refetch(), retrying: all.isFetching } : null
        }
        loading={all.isPending || (list.isPending && !current)}
        readOnly={readOnly}
        onClose={() => openItem(null)}
        onChanged={refresh}
      />
    </div>
  );
}

const uniqueBy = (list: { id: string; name: string }[]) =>
  [...new Map(list.map((x) => [x.id, x])).values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

/** Geometria da matriz gráfica: 5 × 5 casas (impacto na horizontal, urgência na vertical). */
const CHART = { left: 44, top: 12, cell: 60 };
const center = (impact: number, urgency: number) => ({
  x: CHART.left + (impact - 0.5) * CHART.cell,
  y: CHART.top + (5 - urgency + 0.5) * CHART.cell,
});
/** Raio da bolha pelo esforço (1 a 5). */
const radius = (effort: number) => 7 + effort * 4;
/**
 * Área de toque da casa: 28 de raio (56 de diâmetro, dentro da casa de 60). Com a largura mínima do gráfico (300px
 * para 352 unidades), fica com pelo menos 47px na tela — acima dos 44px do celular (DEC-060).
 */
const HIT = 28;

/**
 * Matriz gráfica Impacto × Urgência (§11.5): cada casa com itens mostra uma bolha no centro, maior quanto maior o
 * esforço, com o número da prioridade. Casa com mais de um item mostra a melhor prioridade com "+" e abre a lista dos
 * itens dela para escolher. A tabela traz os mesmos dados.
 */
function MatrixChart({ items, onOpen }: { items: MatrixItem[]; onOpen: (id: string) => void }) {
  const titleId = useId();
  const [cell, setCell] = useState<MatrixItem[] | null>(null);
  const groups = new Map<string, MatrixItem[]>();
  for (const i of items) {
    const key = `${i.impact}:${i.urgency}`;
    groups.set(key, [...(groups.get(key) ?? []), i]);
  }
  const cells = [...groups.values()].map((group) => {
    const sorted = [...group].sort((a, b) => a.priority - b.priority);
    const first = sorted[0]!;
    return {
      items: sorted,
      first,
      effort: Math.max(...sorted.map((i) => i.effort)),
      ...center(first.impact, first.urgency),
    };
  });
  const activate = (group: MatrixItem[]) => (group.length === 1 ? onOpen(group[0]!.id) : setCell(group));
  const onKey = (e: KeyboardEvent<SVGGElement>, group: MatrixItem[]) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate(group);
    }
  };
  const label = (group: MatrixItem[]) => {
    const i = group[0]!;
    return group.length === 1
      ? `${i.priority}º ${i.title}: impacto ${i.impact}, urgência ${i.urgency}, esforço ${i.effort}`
      : `${group.length} itens com impacto ${i.impact} e urgência ${i.urgency}: ${group
          .map((x) => `${x.priority}º ${x.title}`)
          .join('; ')}`;
  };
  const grid = CHART.cell * 5;
  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-2">
      <h2 id={titleId} className="text-base font-bold">
        Matriz Impacto × Urgência
      </h2>
      <p className="text-xs text-muted-foreground">
        Cada bolha mostra a prioridade; quanto maior a bolha, maior o esforço. Com mais de um item na mesma
        casa, a bolha traz a melhor prioridade com “+” e abre a lista. O canto de cima à direita (impacto e
        urgência altos) é o que tratar primeiro.
      </p>
      {/* Largura mínima para os alvos de toque; em tela mais estreita, o gráfico rola dentro da própria região. */}
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${CHART.left + grid + 8} ${CHART.top + grid + 40}`}
          className="mx-auto block w-full max-w-md min-w-[300px]"
          role="group"
          aria-label="Matriz Impacto × Urgência"
        >
          {/* Quadrante de impacto e urgência altos. */}
          <rect
            x={CHART.left + CHART.cell * 3}
            y={CHART.top}
            width={CHART.cell * 2}
            height={CHART.cell * 2}
            className="fill-status-red-bg"
          />
          {Array.from({ length: 6 }, (_, k) => (
            <g key={k} className="stroke-border">
              <line
                x1={CHART.left + k * CHART.cell}
                y1={CHART.top}
                x2={CHART.left + k * CHART.cell}
                y2={CHART.top + grid}
              />
              <line
                x1={CHART.left}
                y1={CHART.top + k * CHART.cell}
                x2={CHART.left + grid}
                y2={CHART.top + k * CHART.cell}
              />
            </g>
          ))}
          {Array.from({ length: 5 }, (_, k) => (
            <g key={`l${k}`} className="fill-muted-foreground text-[11px]">
              <text x={CHART.left + (k + 0.5) * CHART.cell} y={CHART.top + grid + 14} textAnchor="middle">
                {k + 1}
              </text>
              <text x={CHART.left - 8} y={CHART.top + (5 - k - 0.5) * CHART.cell + 4} textAnchor="end">
                {k + 1}
              </text>
            </g>
          ))}
          <text
            x={CHART.left + grid / 2}
            y={CHART.top + grid + 30}
            textAnchor="middle"
            className="fill-foreground text-[12px] font-semibold"
          >
            Impacto →
          </text>
          <text
            x={12}
            y={CHART.top + grid / 2}
            textAnchor="middle"
            transform={`rotate(-90 12 ${CHART.top + grid / 2})`}
            className="fill-foreground text-[12px] font-semibold"
          >
            Urgência →
          </text>
          {cells.map((c) => (
            <g
              key={c.first.id}
              role="button"
              tabIndex={0}
              aria-label={label(c.items)}
              className="cursor-pointer outline-none focus-visible:[&>circle:nth-child(2)]:stroke-ring focus-visible:[&>circle:nth-child(2)]:stroke-[3]"
              onClick={() => activate(c.items)}
              onKeyDown={(e) => onKey(e, c.items)}
            >
              {/* Área de toque. */}
              <circle data-hit cx={c.x} cy={c.y} r={HIT} className="fill-transparent" />
              <circle
                cx={c.x}
                cy={c.y}
                r={radius(c.effort)}
                className="fill-brand/75 stroke-surface"
                strokeWidth={1.5}
              />
              <text
                x={c.x}
                y={c.y + 4}
                textAnchor="middle"
                className="pointer-events-none fill-brand-foreground text-[12px] font-bold"
              >
                {c.first.priority}
                {c.items.length > 1 ? '+' : ''}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <Dialog
        open={cell !== null}
        onOpenChange={(open) => !open && setCell(null)}
        title={cell ? `Impacto ${cell[0]!.impact} · urgência ${cell[0]!.urgency}` : 'Itens da casa'}
        description={cell ? `${cell.length} itens nesta casa da matriz` : undefined}
      >
        <ul aria-label="Itens desta casa" className="flex flex-col gap-2">
          {cell?.map((i) => (
            <li key={i.id}>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  setCell(null);
                  onOpen(i.id);
                }}
              >
                <span className="font-bold tabular">{i.priority}º</span>
                <span className="truncate">{i.title}</span>
                <span className="ml-auto text-xs text-muted-foreground">esforço {i.effort}</span>
              </Button>
            </li>
          ))}
        </ul>
      </Dialog>
    </section>
  );
}

function MatrixItemDialog({
  base,
  organizationId,
  projectId,
  itemId,
  listed,
  matrix,
  loading,
  readOnly,
  onClose,
  onChanged,
}: {
  base: string;
  organizationId: string;
  projectId: string;
  itemId: string | null;
  /** O item como veio na matriz (prioridade e pontuação). */
  listed: MatrixItem | undefined;
  /** Falha ao carregar a matriz (o diálogo mostra o erro de verdade, com nova tentativa). */
  matrix: { error: unknown; retry: () => void; retrying: boolean } | null;
  /** A matriz ainda está carregando. */
  loading: boolean;
  readOnly: boolean;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const [transforming, setTransforming] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const objectives = useQuery({
    queryKey: ['objectives', projectId, 'for-matrix'],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${projectId}/objectives/options`, {
        schema: objectiveOptionsSchema,
        signal,
      }),
    enabled: itemId !== null && canDo('objetivos', 'view') && !readOnly,
  });
  const monitor = useMutation({
    meta: { handlesError: true },
    mutationFn: (id: string) =>
      client.request(`${base}/${id}/monitor`, { method: 'POST', schema: diagnosisItemSchema }),
    onSuccess: async (saved) => {
      toast.success('Item marcado como monitorado', { description: saved.title });
      await onChanged();
    },
    onError: (err) =>
      toast.error('Não foi possível marcar como monitorado', { description: errorMessage(err) }),
  });
  const d = listed;
  const active = Boolean(d) && d?.status !== 'ARCHIVED';
  // Gerar iniciativa e relacionar: "editar" na Matriz (itens da matriz) ou no Diagnóstico (DEC-062).
  const changes = active && (can('priorizacao', 'edit') || can('diagnostico', 'edit'));
  return (
    <>
      <Dialog
        open={itemId !== null && !transforming && !archiving}
        onOpenChange={(open) => !open && onClose()}
        title={d ? `${d.priority}º · ${d.title}` : 'Item da matriz'}
        className="w-[min(46rem,calc(100vw-2rem))]"
      >
        {loading ? (
          <LoadingState rows={4} label="Carregando o item" />
        ) : matrix ? (
          <ErrorState error={matrix.error} onRetry={matrix.retry} retrying={matrix.retrying} />
        ) : !d ? (
          <EmptyState
            icon={Grid3x3}
            title="Item fora da matriz"
            description="Este item não está na matriz: foi arquivado, ainda não foi enviado para ela ou está fora do seu escopo."
          />
        ) : (
          <div className="flex flex-col gap-5 text-sm">
            <p className="rounded-md bg-muted px-3 py-2 tabular">
              Prioridade <strong>{d.priority}º</strong> · pontuação {score(d.score)} (impacto {d.impact} ×
              urgência {d.urgency} ÷ esforço {d.effort})
            </p>
            <ItemSummary item={d} organizationId={organizationId} projectId={projectId} />
            {changes && canDo('objetivos', 'view') ? (
              objectives.isPending ? (
                <LoadingState rows={1} label="Carregando os objetivos" />
              ) : objectives.isError ? (
                <ErrorState
                  error={objectives.error}
                  onRetry={() => void objectives.refetch()}
                  retrying={objectives.isFetching}
                />
              ) : (
                <RelateObjective
                  base={base}
                  item={d}
                  objectives={objectives.data.objectives}
                  onChanged={onChanged}
                />
              )
            ) : null}
            {active ? (
              <section aria-label="Saídas do item" className="flex flex-col gap-2">
                <h3 className="font-bold">Saídas</h3>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  {changes && can('iniciativas', 'create') && !d.initiative ? (
                    <Button variant="outline" onClick={() => setTransforming(true)}>
                      <Rocket aria-hidden />
                      Gerar iniciativa
                    </Button>
                  ) : null}
                  {can('priorizacao', 'edit') && can('planos-acao', 'create') ? (
                    <Button asChild variant="outline">
                      <Link
                        to={`/empresas/${organizationId}/execucao/planos-acao?projeto=${projectId}&matriz=${d.id}`}
                      >
                        <ListChecks aria-hidden />
                        Gerar plano de ação
                      </Link>
                    </Button>
                  ) : null}
                  {can('priorizacao', 'edit') && DIAGNOSIS_MONITOR_FROM.includes(d.status) ? (
                    <Button
                      variant="outline"
                      loading={monitor.isPending}
                      onClick={() => monitor.mutate(d.id)}
                    >
                      <Radar aria-hidden />
                      Marcar como monitorado
                    </Button>
                  ) : null}
                  {can('priorizacao', 'delete') ? (
                    <Button variant="outline" onClick={() => setArchiving(true)}>
                      <Archive aria-hidden />
                      Arquivar
                    </Button>
                  ) : null}
                  {canDo('diagnostico', 'view') ? (
                    <Button asChild variant="ghost">
                      <Link to={`/empresas/${organizationId}/diagnostico?projeto=${projectId}&item=${d.id}`}>
                        <ClipboardList aria-hidden />
                        Ver no Diagnóstico
                      </Link>
                    </Button>
                  ) : null}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </Dialog>
      <TransformInitiativeDialog
        organizationId={organizationId}
        projectId={projectId}
        base={base}
        item={d}
        open={transforming}
        onClose={() => setTransforming(false)}
        onDone={onChanged}
      />
      <ArchiveDialog
        base={base}
        item={d}
        open={archiving}
        onClose={() => setArchiving(false)}
        onDone={async () => {
          setArchiving(false);
          onClose();
          await onChanged();
        }}
      />
    </>
  );
}

/** Arquivar com justificativa obrigatória (saída da matriz). */
function ArchiveDialog({
  base,
  item,
  open,
  onClose,
  onDone,
}: {
  base: string;
  item: MatrixItem | undefined;
  open: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { client } = useAuth();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!open) return;
    setReason('');
    setErrors({});
    setFormError(null);
  }, [open, item?.id]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const id = useId();
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving || !item) return;
    setFormError(null);
    const parsed = diagnosisArchiveInputSchema.safeParse({ reason });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      const saved = await client.request(`${base}/${item.id}/archive`, {
        method: 'POST',
        body: parsed.data,
        schema: diagnosisItemSchema,
      });
      toast.success('Item arquivado', { description: saved.title });
      setReason('');
      await onDone();
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog
      open={open && item !== undefined}
      onOpenChange={(next) => !next && onClose()}
      busy={saving}
      title="Arquivar o item"
      description={item ? `${item.title} sai da matriz e fica só para consulta.` : undefined}
    >
      <form noValidate onSubmit={submit} className="flex flex-col gap-4">
        <FormField id={id} label="Justificativa" error={errors.reason}>
          <textarea
            {...fieldA11y(id, errors.reason)}
            value={reason}
            rows={3}
            maxLength={2000}
            placeholder="Por que o item sai da matriz (resolvido, não se aplica, incorporado a outro…)"
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring aria-invalid:border-danger"
          />
        </FormField>
        {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="danger" loading={saving}>
            Arquivar item
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Filter({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );
}
